import React, { useState, useEffect } from "react";
import styles from "./UserSiteChooserManager.module.css";
import SiteFolderDropdown from "./SiteFolderDropdown";

const UserSiteChooserManager = ({
  selectedSites = [],
  folders = [],
  currentFolderId = null,
  onAddFolder = (isFromAssignment) => {},
  onMoveSites,
  onDeselectAll,
}) => {
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [hasUserSelected, setHasUserSelected] = useState(false);

  useEffect(() => {
    if (selectedSites.length === 0) {
      setSelectedFolder(null);
      setHasUserSelected(false);
      return;
    }

    const folderIds = selectedSites.map((site) => site.folder_id);
    const uniqueFolderIds = [...new Set(folderIds)];

    if (uniqueFolderIds.length !== 1) {
      setSelectedFolder(null);
      setHasUserSelected(true);
      return;
    }

    const commonFolderId = uniqueFolderIds[0];

    if (commonFolderId === null || commonFolderId === undefined) {
      setSelectedFolder("uncategorized");
      setHasUserSelected(false);
      return;
    }

    setSelectedFolder(commonFolderId);
    setHasUserSelected(false);
  }, [selectedSites]);

  if (!selectedSites.length) {
    return (
      <div className={styles["select-instruction"]}>
        <span>Choose a Site to Edit</span>
      </div>
    );
  }

  /**
   * Handle save action - move selected sites to chosen folder
   * @returns {void}
   */
  const handleSave = () => {
    if (selectedFolder && selectedSites.length > 0) {
      const uncategorizedFolder = folders.find((folder) => folder.name === "Uncategorized");
      const isUncategorized =
        selectedFolder === "uncategorized" ||
        (uncategorizedFolder && selectedFolder === uncategorizedFolder.folderId);

      const sitesToMove = selectedSites.filter((site) => {
        const siteFolderId = site.folder_id;
        const targetFolderId = isUncategorized ? null : selectedFolder;

        return siteFolderId !== targetFolderId;
      });

      if (sitesToMove.length > 0) {
        const siteIds = sitesToMove.map((site) => site.site_design_id);
        const folderIds = isUncategorized ? null : selectedFolder;

        onMoveSites({ siteIds, folderIds });
      }

      setSelectedFolder(null);
      setHasUserSelected(false);
    }
  };

  /**
   * Handle folder select
   * @param {Object} option - Option
   * @returns {void}
   */
  const handleFolderSelect = (option) => {
    setSelectedFolder(option.value);

    const folderIds = selectedSites.map((site) => site.folder_id);
    const uniqueFolderIds = [...new Set(folderIds)];

    if (uniqueFolderIds.length !== 1) {
      setHasUserSelected(true);
      return;
    }

    const commonFolderId = uniqueFolderIds[0];
    const expectedFolderId =
      commonFolderId === null || commonFolderId === undefined ? "uncategorized" : commonFolderId;

    if (option.value === expectedFolderId) {
      setHasUserSelected(false);
      return;
    }

    setHasUserSelected(true);
  };

  /**
   * Handle cancel action
   * @returns {void}
   */
  const handleCancel = () => {
    setHasUserSelected(false);

    if (selectedSites.length === 0) {
      setSelectedFolder(null);
      setHasUserSelected(false);
      return;
    }

    const folderIds = selectedSites.map((site) => site.folder_id);
    const uniqueFolderIds = [...new Set(folderIds)];

    if (uniqueFolderIds.length !== 1) {
      setSelectedFolder(null);
      setHasUserSelected(true);
      return;
    }

    const commonFolderId = uniqueFolderIds[0];

    if (commonFolderId === null || commonFolderId === undefined) {
      setSelectedFolder("uncategorized");
      setHasUserSelected(false);
      return;
    }

    setSelectedFolder(commonFolderId);
    setHasUserSelected(false);
  };

  /**
   * Handle add folder action
   * @returns {void}
   */
  const handleAddFolder = () => {
    onAddFolder(true);
  };

  return (
    <div className={styles["site-manager-wrapper"]}>
      <span>{`${selectedSites.length} Selected`}</span>

      <button
        className={styles["deselect-all-button"]}
        onClick={onDeselectAll}
        title="Deselect all sites"
      >
        ×
      </button>

      <SiteFolderDropdown
        variant="assignment"
        options={folders}
        selectedValue={selectedFolder}
        onSelect={handleFolderSelect}
        placeholder="Move to..."
        showActionButtons={folders.length > 0}
        onAddFolder={handleAddFolder}
        onSave={handleSave}
        onCancel={handleCancel}
        disableSaveButton={!hasUserSelected}
      />
    </div>
  );
};

export default UserSiteChooserManager;
